import { Button, ButtonGroup, CloseButton, TextButton } from '@zillow/constellation';
import { useEffect, useState } from 'react';

import { StyledModalDialog } from './StyledComponents';
import UpsellBody from './UpsellBody';
import UpsellSurveyBody from './UpsellSurveyBody';
import UpsellWidgetBody from './UpsellWidgetBody';
import EmbeddedWidget from '../EmbeddedWidget';
import { clickUpsell, optOutFromUpsell } from '../../api/api';
import useWidget from '../../hooks/useWidget';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';
import { CLICK_LEASE_SURVEY_SUBMIT, CLICK_LEASE_UPSELL, CLICK_WIDGET_CLOSE } from '../../utils/ClickstreamEvents';
import ZillowInsuranceServicesLogo from '../ZillowInsuranceServicesLogo';

import type { TrackingIdType, UpsellEntryPointType, UpsellInfo } from '../../types';

import { HomesiteConstants } from '../../constants';

type UpsellModalProps = {
  apiUrl: string;
  isOpen: boolean;
  trackingIdType: TrackingIdType;
  upsellEntryPointType: UpsellEntryPointType;
  upsellInfo: UpsellInfo;
  onClose: () => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const NO_RESPONSE_REASON = 'noResponse';

const UpsellModal = ({
  apiUrl,
  isOpen,
  trackingIdType,
  upsellEntryPointType,
  upsellInfo,
  onClose,
  trackClickstreamEvent,
}: UpsellModalProps) => {
  const [showUpsell, setShowUpsell] = useState(true);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showWidget, setShowWidget] = useState(false);
  const [upsellWidgetUrl, setUpsellWidgetUrl] = useState('');
  const [surveyResponse, setSurveyResponse] = useState({ declineReason: '', order: 0 });

  useWidget(upsellWidgetUrl);

  useEffect(() => {
    const { renderType, widgetFields } = upsellInfo;

    if (renderType === 'widget' && widgetFields?.widgetUrl) {
      setUpsellWidgetUrl(widgetFields.widgetUrl);
    }
  }, []);

  const openWidget = () => {
    setShowUpsell(false);
    setShowWidget(true);
  };

  const closeUpsell = () => {
    setShowUpsell(false);
    setShowSurvey(true);
  };

  const hideModal = () => {
    setShowUpsell(true);
    setShowSurvey(false);
    setShowWidget(false);
    setSurveyResponse({ declineReason: '', order: 0 });
  };

  const closeWidget = () => {
    const getQuoteButton = document.querySelector(`#${HomesiteConstants.WIDGET_BUTTON_IDS.GET_QUOTE}`);
    const reviewCoverageButton = document.querySelector(`#${HomesiteConstants.WIDGET_BUTTON_IDS.REVIEW_COVERAGE}`);

    if (getQuoteButton || reviewCoverageButton) {
      const screenName = getQuoteButton ? 'get_quote' : 'quote_summary';
      trackClickstreamEvent(CLICK_WIDGET_CLOSE(screenName));
    }
    return closeModal();
  };

  const closeModal = () => {
    hideModal();
    return onClose();
  };

  const onClickUpsell = async () => {
    trackClickstreamEvent(CLICK_LEASE_UPSELL(upsellEntryPointType));
    await clickUpsell(upsellInfo.upsellId, trackingIdType, apiUrl);

    if (upsellWidgetUrl) {
      return openWidget();
    }

    window.open(upsellInfo.redirectUrl, '_blank');
    return closeModal();
  };

  const camelCaseToSnakeCase = (string: string) => string.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

  const submitSurvey = async () => {
    const reason = surveyResponse.declineReason || NO_RESPONSE_REASON;
    const shouldHide = reason === 'alreadyEnrolled' || reason === 'wontEnrollZillow';
    const snakeCaseReason = camelCaseToSnakeCase(reason);
    trackClickstreamEvent(CLICK_LEASE_SURVEY_SUBMIT(snakeCaseReason, surveyResponse.order));
    await optOutFromUpsell(upsellInfo.upsellId, reason, shouldHide, apiUrl);
    return closeModal();
  };

  const getBodyToRender = () => {
    if (showUpsell) {
      return upsellWidgetUrl ? <UpsellWidgetBody upsellInfo={upsellInfo} /> : <UpsellBody upsellInfo={upsellInfo} />;
    } else if (showSurvey) {
      return <UpsellSurveyBody onSurveyResponse={setSurveyResponse} />;
    } else if (showWidget) {
      return (
        <EmbeddedWidget
          city={upsellInfo.widgetFields?.city}
          email={upsellInfo.widgetFields?.email}
          firstName={upsellInfo.widgetFields?.firstName}
          lastName={upsellInfo.widgetFields?.lastName}
          params={upsellInfo.widgetFields?.params}
          state={upsellInfo.widgetFields?.state}
          street={upsellInfo.widgetFields?.street}
          zip={upsellInfo.zip}
          onClose={closeModal}
          trackClickstreamEvent={trackClickstreamEvent}
        />
      );
    }
  };

  const getFooter = () => {
    if (showSurvey) {
      return (
        <Button data-testid="upsell-modal-survey-submit-btn" buttonType="primary" onClick={submitSurvey}>
          Go to my leases
        </Button>
      );
    } else if (showWidget) {
      return null;
    }

    return (
      <ButtonGroup aria-label="Modal actions">
        <TextButton data-testid="upsell-modal-survey-no-thanks-btn" onClick={closeUpsell}>
          No thanks
        </TextButton>
        <Button data-testid="upsell-modal-click-upsell-btn" buttonType="primary" onClick={onClickUpsell}>
          Get started
        </Button>
      </ButtonGroup>
    );
  };

  return (
    <StyledModalDialog
      isOpen={isOpen}
      size={{ default: 'md', md_lte: 'fullScreen' }}
      renderHeader={<ZillowInsuranceServicesLogo />}
      renderBody={getBodyToRender()}
      renderFooter={getFooter()}
      renderCloseButton={showWidget && <CloseButton />}
      onClose={closeWidget}
      shouldCloseOnESCKeyPress={false}
      shouldCloseOnOutsideClick={false}
    />
  );
};

export default UpsellModal;
